import { useEffect, useState, Fragment, useRef } from 'react';
import { userAtom } from '@/global-store';
import { Collapse } from '@mui/material';
import { IconChevronDown, IconChevronUp } from '@tabler/icons-react';
import { useAtom } from 'jotai';
import { useLocation } from 'react-router';
import { allowedToShowItem } from './allowedToShowItem';
import NavItem from '@layout/Sidebar/MenuList/NavItem';
import { useSidebar } from '@layout/Sidebar/SidebarContext';
import SidebarNavButton from '@layout/Sidebar/MenuList/SidebarNavButton';
import NavItemLabel from '@layout/Sidebar/MenuList/NavItemLabel';
import { useNavigate } from 'react-router-dom';
import type { MenuItem } from '@layout/Sidebar/MenuList/types';
import { useInlineTranslation } from '@/i18n/hooks/useInlineTranslation';
import { T } from '@/i18n/T';

type NavCollapseProps = {
  item: MenuItem;
  level: number;
};

function NavCollapse({ item, level }: NavCollapseProps) {
  const { t } = useInlineTranslation();
  const [user] = useAtom(userAtom);
  const { isMobile } = useSidebar();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const defaultExpanded = item.handle.defaultExpanded || false;
  const [open, setOpen] = useState(defaultExpanded);
  const buttonRef = useRef<HTMLButtonElement>(null);

  const menus = (item.children ?? []).filter((child) =>
    allowedToShowItem(child.handle.access, user, child.path),
  );

  const title = item.handle.i18nNameKey ? (
    <T k={item.handle.i18nNameKey} />
  ) : (
    (item.handle.name ?? '')
  );
  const collapseId = `nav-collapse-${item.path.replaceAll('/', '-')}`;

  useEffect(() => {
    if (defaultExpanded) {
      setOpen(true);
      return;
    }

    const matchesPath = (child: MenuItem) => {
      return (
        Boolean(child.path) &&
        pathname.includes(child.path.replace(/\/:[^]*/g, ''))
      );
    };

    const hasMatchingChild = (children?: MenuItem[]): boolean => {
      return (children ?? []).some(
        (child) => matchesPath(child) || hasMatchingChild(child.children),
      );
    };

    setOpen(hasMatchingChild(item.children));
  }, [defaultExpanded, item.children, pathname]);

  const handleClick = () => {
    if (open) {
      setOpen(false);
    } else {
      setOpen(true);

      // For desktop, automatically navigate to the first visible subitem.
      // Uses the filtered menu (not raw children) so hidden routes — e.g. the
      // section's index redirect — are skipped regardless of their position.
      if (!isMobile && menus[0]?.path) {
        void navigate(menus[0].path);
      }
    }
  };

  const handleEscapeCollapse = () => {
    setOpen(false);

    buttonRef.current?.focus();
  };

  return (
    <Fragment>
      <SidebarNavButton
        component='button'
        level={level}
        aria-expanded={open ? 'true' : 'false'}
        aria-controls={collapseId}
        onClick={handleClick}
        ref={buttonRef}
        tooltip={
          item.handle.i18nTooltipKey ? t(item.handle.i18nTooltipKey) : undefined
        }
      >
        <NavItemLabel item={item} title={title} />

        <div className='eye:ml-auto'>
          {open ? <IconChevronUp /> : <IconChevronDown />}
        </div>
      </SidebarNavButton>
      <Collapse id={collapseId} in={open} timeout='auto' unmountOnExit>
        <ul>
          {menus.map((child) => (
            <li key={child.path}>
              <NavItem
                item={child}
                level={level + 1}
                onEscapeCollapse={handleEscapeCollapse}
              />
            </li>
          ))}
        </ul>
      </Collapse>
    </Fragment>
  );
}

export default NavCollapse;
